const apikey = "e3f075f1eb4f0bf241539b1de626c79acac64188c5c3ee817e3356f6bcf3aa3b"; // zentry.innocode.co
const serverUrl = "https://fido2-core.innocode.co";

function base64urlToArrayBuffer(b64url: string): ArrayBuffer {
    const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
    const b64 = (b64url.replace(/-/g, '+').replace(/_/g, '/')) + pad;
    const raw = atob(b64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
    return arr.buffer;
}

function arrayBufferToBase64url(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    const b64 = btoa(binary);
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

interface ServerCreationOptions {
    rp: { name: string; id?: string };
    user: { id: string; name: string; displayName: string }; // id는 base64url 문자열
    challenge: string; // base64url 문자열
    pubKeyCredParams: Array<{ type: PublicKeyCredentialType; alg: number }>;
    timeout?: number;
    attestation?: AttestationConveyancePreference;
    authenticatorSelection?: {
        requireResidentKey?: boolean;
        userVerification?: UserVerificationRequirement;
        authenticatorAttachment?: AuthenticatorAttachment;
        residentKey?: 'required' | 'preferred' | 'discouraged';
    };
    hints?: any;
    excludeCredentials?: Array<{
        id: string; // base64url 문자열
        type: PublicKeyCredentialType;
        transports?: AuthenticatorTransport[];
    }>;
    extensions?: AuthenticationExtensionsClientInputs;
}

interface ServerCreationOptionsRequest {
    username: string;
    displayName: string;
    authenticatorSelection?: {
        requireResidentKey?: boolean;
        userVerification?: UserVerificationRequirement;
        authenticatorAttachment?: AuthenticatorAttachment;
    };
    attestation?: AttestationConveyancePreference | "";
    credProtect?: {
        credentialProtectionPolicy?: 'userVerificationOptional' | 'userVerificationOptionalWithCredentialIDList' | 'userVerificationRequired';
        enforceCredentialProtectionPolicy?: boolean;
    };
}

function toClientCreationOptions(server: ServerCreationOptions): PublicKeyCredentialCreationOptions {
    // 인증장치 재등록 허용시 아래 주석
    const excludeCredentials: PublicKeyCredentialDescriptor[] | undefined = server.excludeCredentials?.map(ec => ({
        type: ec.type,
        id: base64urlToArrayBuffer(ec.id),
        transports: ec.transports,
    }));

    const authenticatorSelection: AuthenticatorSelectionCriteria | undefined = server.authenticatorSelection
        ? {
            requireResidentKey: server.authenticatorSelection.requireResidentKey,
            userVerification: server.authenticatorSelection.userVerification,
            authenticatorAttachment: server.authenticatorSelection.authenticatorAttachment,
            residentKey: server.authenticatorSelection.residentKey,
        }
        : undefined;

    return {
        rp: server.rp,
        user: {
            id: new Uint8Array(base64urlToArrayBuffer(server.user.id)),
            name: server.user.name,
            displayName: server.user.displayName,
        },
        challenge: base64urlToArrayBuffer(server.challenge),
        pubKeyCredParams: server.pubKeyCredParams,
        timeout: server.timeout,
        attestation: server.attestation,
        authenticatorSelection,
        excludeCredentials,
        extensions: server.extensions,
    };
}

interface ServerGetOptions {
    status: "ok" | "failed";
    errorMessage?: string;
    challenge: string; // base64url
    timeout?: number;
    rpId?: string;
    allowCredentials?: Array<{
        id: string; // base64url
        type: PublicKeyCredentialType;
        transports?: AuthenticatorTransport[];
    }>;
    userVerification?: UserVerificationRequirement;
    extensions?: AuthenticationExtensionsClientInputs;
}

async function rest_post<T>(url: string, body: unknown): Promise<T> {
    const endpoint = serverUrl + url;
    const res = await fetch(endpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Apikey": apikey
        },
        // credentials: "same-origin",
        credentials: "include",
        body: JSON.stringify(body),
    });

    // if (!res.ok) {
    //     // throw new Error(`POST ${url} failed: ${res.status}`);
    //     console.error(`POST ${url} failed: ${res.status}`);
    // }

    return (await res.json()) as T;
}

function toClientGetOptions(server: ServerGetOptions): PublicKeyCredentialRequestOptions {
    const allowCredentials: PublicKeyCredentialDescriptor[] | undefined =
        server.allowCredentials?.map((c) => ({
            type: c.type,
            id: base64urlToArrayBuffer(c.id),
            transports: c.transports,
        }));

    return {
        challenge: base64urlToArrayBuffer(server.challenge),
        timeout: server.timeout,
        rpId: server.rpId,
        allowCredentials,
        userVerification: server.userVerification,
        extensions: server.extensions,
    };
}

async function rest_delete<T>(url: string): Promise<T> {
    const endpoint = serverUrl + url;
    const res = await fetch(endpoint, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            "Apikey": apikey
        },
        // credentials: "same-origin",
        credentials: "include"
    });

    // if (!res.ok) {
    //     // throw new Error(`POST ${url} failed: ${res.status}`);
    //     console.error(`POST ${url} failed: ${res.status}`);
    // }
    return (await res.json()) as T;
}

async function rest_get<T>(url: string): Promise<T> {
    const endpoint = serverUrl + url;
    const res = await fetch(endpoint, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "Apikey": apikey
        },
        // credentials: "same-origin",
        credentials: "include"
    });

    // if (!res.ok) {
    //     // throw new Error(`POST ${url} failed: ${res.status}`);
    //     console.error(`POST ${url} failed: ${res.status}`);
    // }
    return (await res.json()) as T;
}
export class Fido2 {
    // 취소 제어
    private static ctrl: AbortController | null = null;

    // 사용자 등록 버튼 클릭 이벤트
    static async registerButtonClicked(userName: string, options?: { authenticatorAttachment?: AuthenticatorAttachment }): Promise<string> {
        const username = userName;
        const displayName = userName;

        // fido2 옵션 설정
        const specifyAuthenticatorSelection = true;
        const specifyAuthenticatorAttachment = false;
        // const attachment = 'cross-platform'; // "platform"(노트북 지문인증) | "cross-platform"(패스키/usb 지문인증) | ""()
        const attachment = options?.authenticatorAttachment;

        const requireResidentKey = true;
        const userVerification: UserVerificationRequirement = "preferred";

        const specifyAttestationConveyance = true;
        const attestation: AttestationConveyancePreference = "direct";

        const enableCredProtect = false;
        const enforceCredentialProtectionPolicy = false;
        const credentialProtectionPolicy:
            | "userVerificationOptional"
            | "userVerificationOptionalWithCredentialIDList"
            | "userVerificationRequired" = "userVerificationOptional";

        const reqBody: ServerCreationOptionsRequest = {
            username,
            displayName,
            authenticatorSelection: {},
            attestation: "",
            credProtect: {},
        };

        if (specifyAuthenticatorSelection) {
            const authenticatorSelection: ServerCreationOptionsRequest["authenticatorSelection"] = {
                requireResidentKey,
                userVerification
            };

            if (specifyAuthenticatorAttachment) {
                authenticatorSelection.authenticatorAttachment = attachment;
            };

            reqBody.authenticatorSelection = authenticatorSelection;
        } else {
            delete reqBody.authenticatorSelection; // 빈 객체 방지
        }

        if (specifyAttestationConveyance) {
            reqBody.attestation = attestation; // 개인정보 보호 목적상 none 권장
        } else {
            delete reqBody.attestation;
        }

        if (enableCredProtect) {
            reqBody.credProtect = {
                credentialProtectionPolicy,
                enforceCredentialProtectionPolicy,
            };
        } else {
            delete reqBody.credProtect;
        }

        const serverOptions = await this.getRegChallenge(reqBody);

        const publicKeyOptions = toClientCreationOptions(serverOptions);

        // 크리덴셜 생성
        const attestationResult = await this.createCredential(publicKeyOptions);

        if (attestationResult) {
            // 서버에 결과 전송
            const result = await this.sendAttestationResult(attestationResult);
            return JSON.stringify(result);
        } else {
            return "The registration has been cancelled.";
        }
    }

    // 서버에 attestation/options 요청
    static async getRegChallenge(
        payload: ServerCreationOptionsRequest
    ): Promise<ServerCreationOptions> {
        const endpoint = serverUrl + "/attestation/options";
        const res = await fetch(endpoint, {
            method: "POST",
            // credentials: "same-origin",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "Apikey": apikey
            },
            body: JSON.stringify(payload),
        });

        // if (!res.ok) {
        //     const text = await res.text().catch(() => "");
        //     throw new Error(`Options fetch failed: ${res.status} ${text}`);
        //     // console.error(`Options fetch failed: ${res.status} ${text}`);
        // }

        const data = (await res.json()) as ServerCreationOptions;
        return data;
    }

    // 크리덴셜 생성
    static async createCredential(
        options: PublicKeyCredentialCreationOptions
    ) {
        // //에러 컨트롤 설정
        this.ctrl = new AbortController();
        const abortSignal = this.ctrl.signal;

        if (
            typeof window === "undefined" ||
            !("PublicKeyCredential" in window) ||
            typeof (PublicKeyCredential as any).isUserVerifyingPlatformAuthenticatorAvailable !==
            "function"
        ) {
            // throw new Error("WebAuthn APIs are not available on this user agent.");
            // console.error("WebAuthn APIs are not available on this user agent.");
            // return ("WebAuthn APIs are not available on this user agent.");
            return ("The device cannot be used with an authenticator.");
        }

        try {
            // localhost는 http 허용. 그 외는 https 필요.
            const cred = (await navigator.credentials.create({
                publicKey: options,
                signal: abortSignal,
            })) as PublicKeyCredential;

            if (!cred) throw new Error('credential create returned null');

            const attestationResponse = cred.response as AuthenticatorAttestationResponse;

            // 2) transports 안전 추출
            const transports: AuthenticatorTransport[] =
                typeof (attestationResponse as any).getTransports === 'function'
                    ? Array.from((attestationResponse as any).getTransports())
                    : [];

            // 3) authenticatorAttachment 포함 (WebAuthn L3)
            const authenticatorAttachment =
                (cred as any).authenticatorAttachment || undefined; // "platform" | "cross-platform" | undefined


            // 4) client hints → payload에 함께 전송, authenticator 구분을 위한 보조 장치
            const clientHints = {
                ua: navigator.userAgent || '',
                platform: (navigator as any).userAgentData?.platform || navigator.platform || '',
                platformVersion: (navigator as any).userAgentData?.platformVersion || '',
                mobile:
                    (navigator as any).userAgentData?.mobile ??
                    /Android|iPhone|iPad/i.test(navigator.userAgent),
                brands: ((navigator as any).userAgentData?.brands || []).map((b: any) => `${b.brand}/${b.version}`),
            };

            // base64url 인코딩하여 서버로 전송
            const result = {
                id: cred.id,
                rawId: arrayBufferToBase64url(cred.rawId),
                type: cred.type,
                response: {
                    clientDataJSON: arrayBufferToBase64url(attestationResponse.clientDataJSON),
                    attestationObject: arrayBufferToBase64url(attestationResponse.attestationObject),
                    transports,
                },
                clientExtensionResults:
                    typeof cred.getClientExtensionResults === "function"
                        ? cred.getClientExtensionResults()
                        : undefined,

                authenticatorAttachment, // ← 추가
                clientHints,             // ← 추가
            };

            return result;

        } catch (e: any) {
            // const aborted = this.ctrl.signal.aborted;
            // const abortReason = (this.ctrl.signal as any).reason;

            // let reason: 'user_cancel' | 'already_registered' | 'timeout' | 'origin_or_rpId' | 'denied_or_unavailable' | 'unknown';

            // if (aborted && abortReason === 'user_cancel' && this.userCancelled) {
            //     reason = 'user_cancel';
            // } else if (aborted && (abortReason === 'timeout')) {
            //     reason = 'timeout';
            // } else if (e?.name === 'InvalidStateError') {
            //     reason = 'already_registered';
            // } else if (e?.name === 'NotAllowedError') {
            //     // 일부 환경은 exclude 충돌도 빠른 NotAllowed로 떨어짐
            //     reason = 'already_registered';
            // } else if (e?.name === 'SecurityError') {
            //     reason = 'origin_or_rpId'; // HTTPS/rpId/origin 문제
            // } else if (e?.name === 'NotAllowedError') {
            //     reason = 'denied_or_unavailable'; // 장치 거절/사용자 닫음/가용X 등
            // } else if (e?.name === 'AbortError') {
            //     reason = this.userCancelled ? 'user_cancel' : 'unknown';
            // } else {
            //     reason = 'unknown';
            // }
            return null;
        } finally {
            // clearTimeout(t);
            this.ctrl = null;
        }
    }

    // attestation 결과 서버 전송
    static async sendAttestationResult(attestation: any): Promise<string> {
        const endpoint = serverUrl + "/attestation/result";
        const res = await fetch(endpoint, {
            method: "POST",
            // credentials: "same-origin",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "Apikey": apikey
            },
            body: JSON.stringify(attestation),
        });

        // if (!res.ok) {
        //     const text = await res.text().catch(() => "");
        //     // throw new Error(`Attestation submit failed: ${res.status} ${text}`);
        //     console.error(`Attestation submit failed: ${res.status} ${text}`);
        // }

        return (await res.json());
    }

    // 사용자 인증 버튼 클릭 이벤트
    static async authenticateButtonClicked(userName: string): Promise<string> {
        const username = userName;
        const userVerification: UserVerificationRequirement = "preferred"; // or "required" / "discouraged"

        const requestPayload = {
            username,
            userVerification,
        };

        try {
            // 서버에서 challenge 옵션 받기
            const serverOptions = await rest_post<ServerGetOptions>(
                "/assertion/options",
                requestPayload
            );

            if (serverOptions.status !== "ok") {
                // throw new Error(serverOptions.errorMessage || "Unknown server error");
                return (serverOptions.errorMessage || "Unknown server error");
            }

            const clientOptions = toClientGetOptions(serverOptions);

            // WebAuthn 실행
            const assertion = await this.getAssertion(clientOptions);

            if (assertion) {
                // 검증 결과 서버 전송
                const result = await rest_post("/assertion/result", assertion);
                return JSON.stringify(result);
            } else {
                return "The authentication has been cancelled.";
            }
        } catch (e) {
            console.error("Authenticate error:", e);
            return JSON.stringify(e);
        }
    }

    static async getAssertion(
        options: PublicKeyCredentialRequestOptions
    ): Promise<{
        id: string;
        rawId: string;
        type: PublicKeyCredential["type"];
        response: {
            clientDataJSON: string;
            authenticatorData: string;
            signature: string;
            userHandle: string | null;
        };
        clientExtensionResults?: AuthenticationExtensionsClientOutputs;
    } | null> {
        // //에러 컨트롤 설정
        this.ctrl = new AbortController();

        try {
            const credential = (await navigator.credentials.get({ //webAuthen 호출
                publicKey: options,
            })) as PublicKeyCredential;

            const resp = credential.response as AuthenticatorAssertionResponse;

            return {
                id: credential.id,
                rawId: arrayBufferToBase64url(credential.rawId),
                type: credential.type,
                response: {
                    clientDataJSON: arrayBufferToBase64url(resp.clientDataJSON),
                    authenticatorData: arrayBufferToBase64url(resp.authenticatorData),
                    signature: arrayBufferToBase64url(resp.signature),
                    userHandle: resp.userHandle ? arrayBufferToBase64url(resp.userHandle) : null,
                },
                clientExtensionResults: credential.getClientExtensionResults(),
            };
        } catch (e: any) {
            // const aborted = this.ctrl.signal.aborted;
            // const abortReason = (this.ctrl.signal as any).reason;

            // let reason: 'user_cancel' | 'already_registered' | 'timeout' | 'origin_or_rpId' | 'denied_or_unavailable' | 'unknown';

            // if (aborted && abortReason === 'user_cancel' && this.userCancelled) {
            //     reason = 'user_cancel';
            // } else if (aborted && (abortReason === 'timeout')) {
            //     reason = 'timeout';
            // } else if (e?.name === 'InvalidStateError') {
            //     reason = 'already_registered';
            // } else if (e?.name === 'NotAllowedError') {
            //     // 일부 환경은 exclude 충돌도 빠른 NotAllowed로 떨어짐
            //     reason = 'already_registered';
            // } else if (e?.name === 'SecurityError') {
            //     reason = 'origin_or_rpId'; // HTTPS/rpId/origin 문제
            // } else if (e?.name === 'NotAllowedError') {
            //     reason = 'denied_or_unavailable'; // 장치 거절/사용자 닫음/가용X 등
            // } else if (e?.name === 'AbortError') {
            //     reason = this.userCancelled ? 'user_cancel' : 'unknown';
            // } else {
            //     reason = 'unknown';
            // }

            // return { ok: false, name: e?.name, message: e?.message, reason, elapsedMs: Math.round(elapsed) };
            return null;
        } finally {
            // clearTimeout(t);
            this.ctrl = null;
        }
    }

    static async deleteButtonClicked(credId: string): Promise<string> {
        const endpoint = "/credentials/" + credId;
        const result = await rest_delete(endpoint);

        return JSON.stringify(result);
    }


    static async findUserNameCredentials(userName: string): Promise<string> {
        const endpoint = "/credentials?username=" + userName;
        const result = await rest_get(endpoint);

        return JSON.stringify(result);
    }
}